import { useTrackToggle } from '@livekit/components-react';
import { Track } from 'livekit-client';
import {
  LogOut, Mic, MicOff, MonitorUp, Video, VideoOff,
} from 'lucide-react-native';
import { useCallback, type ReactNode } from 'react';
import { Platform, Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';
import {
  Box, HStack, Icon, Text,
} from '@gluestack-ui/themed';

import { MediaPill } from './MediaPill';
import { useMeetingConfirm } from './MeetingConfirm';
import { useMeetingRoom } from './MeetingRoomContext';

export type MeetingLeaveOptions = {
  /** When true, Leave disconnects immediately (no destructive confirm). */
  skipLeaveConfirm?: boolean;
  /**
   * When set, runs before disconnect. Return false to stay connected;
   * true/void proceeds to leaveMeeting().
   */
  beforeLeave?: () => Promise<boolean | void>;
};

/** Shared leave flow for the control bar and connection-lost recovery. */
export function useMeetingLeaveAction(
  leaveMeeting: () => void,
  options: MeetingLeaveOptions = {},
) {
  const { t } = useTranslation();
  const { confirm } = useMeetingConfirm();
  const { skipLeaveConfirm = false, beforeLeave } = options;

  return useCallback(async () => {
    if (beforeLeave) {
      const approved = await beforeLeave();
      if (approved !== false) leaveMeeting();
      return;
    }
    if (skipLeaveConfirm) {
      leaveMeeting();
      return;
    }
    const confirmed = await confirm({
      title: t('meeting.leaveConfirmTitle', { defaultValue: 'Leave the meeting?' }),
      message: t('meeting.leaveConfirmMessage', {
        defaultValue: 'You will disconnect from the room. You can rejoin if the session is still open.',
      }),
      confirmLabel: t('meeting.leave'),
      destructive: true,
    });
    if (confirmed) leaveMeeting();
  }, [beforeLeave, confirm, leaveMeeting, skipLeaveConfirm, t]);
}

export type MeetingControlBarProps = {
  /**
   * Optional session-specific controls (e.g. an oral exam's Next / Abort / End)
   * rendered to the right of the AV controls, separated by a divider, so the
   * meeting has a single cohesive bottom bar rather than two stacked rows.
   */
  conductingControls?: ReactNode;
  /** Hide screen-share toggle (1:1 conducting omits share). */
  hideScreenShare?: boolean;
  /** Hide Leave control (1:1 conducting keeps Abort only). */
  hideLeave?: boolean;
} & MeetingLeaveOptions;

/**
 * Bottom control bar: mic/camera toggles with device pickers, screen share, leave.
 * When `conductingControls` is provided they share this bar after a divider.
 */
export function MeetingControlBar({
  conductingControls,
  skipLeaveConfirm = false,
  beforeLeave,
  hideScreenShare = false,
  hideLeave = false,
}: MeetingControlBarProps = {}) {
  const { t } = useTranslation();
  const { leaveMeeting } = useMeetingRoom();
  const isWeb = Platform.OS === 'web';

  const screenToggle = useTrackToggle({
    source: Track.Source.ScreenShare,
    captureOptions: { audio: false },
  });

  const handleLeave = useMeetingLeaveAction(leaveMeeting, { skipLeaveConfirm, beforeLeave });

  return (
    <HStack
      justifyContent="center"
      alignItems="center"
      space="md"
      py="$3"
      px="$4"
      testID="meeting-control-bar"
    >
      <MediaPill
        source={Track.Source.Microphone}
        kind="audioinput"
        enabledIcon={Mic}
        disabledIcon={MicOff}
        testID="meeting-mic-toggle"
      />
      <MediaPill
        source={Track.Source.Camera}
        kind="videoinput"
        enabledIcon={Video}
        disabledIcon={VideoOff}
        testID="meeting-cam-toggle"
      />
      {isWeb && !hideScreenShare ? (
        <Pressable onPress={() => { screenToggle.toggle(); }} testID="meeting-screen-share">
          <Box
            px="$3"
            py="$2"
            borderRadius="$full"
            borderWidth={1}
            borderColor="$borderLight"
            bg={screenToggle.enabled ? '$primary100' : '$bgApp'}
          >
            <Icon as={MonitorUp} size="sm" color="$textHeading" />
          </Box>
        </Pressable>
      ) : null}
      {!hideLeave ? (
        <Pressable onPress={() => { handleLeave().catch(() => undefined); }} testID="meeting-leave">
          <HStack
            px="$4"
            py="$2"
            borderRadius="$full"
            bg="$statusError"
            alignItems="center"
            space="xs"
          >
            <Icon as={LogOut} size="sm" color="$textInverted" />
            <Text size="sm" color="$textInverted" fontWeight="semibold">
              {t('meeting.leave')}
            </Text>
          </HStack>
        </Pressable>
      ) : null}
      {conductingControls ? (
        <>
          <Box w={1} h={28} bg="$borderLight" mx="$1" />
          {conductingControls}
        </>
      ) : null}
    </HStack>
  );
}
